import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Activity, Truck, MapPin, CheckCircle2, AlertTriangle, PackageX, Plus, Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { api } from "@/lib/api";
import { statusInfo, toneOf } from "@/lib/statuses";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/tracking/StatusBadge";
import { PageTitle } from "@/components/admin/AdminLayout";
import { DelayPanel } from "@/components/admin/DelayPanel";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const KPIS = [
  { key: "total", label: "Totali", icon: Package, q: "archived=all", accent: "text-slate-900" },
  { key: "active", label: "Attive", icon: Activity, q: "", accent: "text-[#E50914]" },
  { key: "in_transit", label: "In transito", icon: Truck, q: "status=IN_TRANSITO", accent: "text-blue-600" },
  { key: "in_delivery", label: "In consegna", icon: MapPin, q: "status=IN_CONSEGNA", accent: "text-amber-600" },
  { key: "delivered", label: "Consegnate", icon: CheckCircle2, q: "status=CONSEGNATA&archived=all", accent: "text-emerald-600" },
  { key: "delayed", label: "In ritardo", icon: AlertTriangle, q: "status=DELAYED", accent: "text-red-600" },
  { key: "in_storage", label: "In giacenza", icon: PackageX, q: "status=GIACENZA", accent: "text-orange-600" },
];

const Card = ({ title, children, className = "" }) => (
  <div className={`bg-white border border-slate-200 p-5 sm:p-6 ${className}`}>
    {title && <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">{title}</h2>}
    {children}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="py-32 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E50914]" /></div>;
  const monthly = stats.monthly.map((m) => ({ ...m, label: `${MONTHS[+m.month.slice(5) - 1]} ${m.month.slice(2, 4)}` }));
  const byStatus = stats.by_status.filter((s) => s.count > 0).map((s) => ({ ...s, label: statusInfo(s.status).label, fill: toneOf(s.status).hex }));

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Dashboard" subtitle="Panoramica delle spedizioni aziendali">
        <Button onClick={() => navigate("/admin/spedizioni/nuova")} className="bg-[#E50914] hover:bg-[#C10710] rounded-none" data-testid="dashboard-new-shipment-btn"><Plus className="h-4 w-4 mr-1" /> Nuova spedizione</Button>
      </PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 sm:gap-4">
        {KPIS.map((k) => (
          <Link key={k.key} to={`/admin/spedizioni?${k.q}`} className="group bg-white border border-slate-200 p-4 sm:p-5 hover:border-slate-900 transition-colors" data-testid={`kpi-${k.key}`}>
            <k.icon className={`h-5 w-5 ${k.accent}`} />
            <p className={`mt-3 font-display text-4xl font-black ${k.accent}`} data-testid={`kpi-${k.key}-value`}>{stats[k.key]}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k.label}</p>
          </Link>
        ))}
      </div>
      <DelayPanel onRun={() => api.get("/admin/stats").then((r) => setStats(r.data))} />
      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Spedizioni per mese (data affidamento)">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={30} />
                <Tooltip formatter={(v) => [v, "Spedizioni"]} />
                <Bar dataKey="count" fill="#E50914" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Distribuzione per stato">
          {byStatus.length === 0 ? <p className="text-sm text-slate-500 h-64 flex items-center justify-center">Nessuna spedizione registrata.</p> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byStatus} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, "Spedizioni"]} />
                  <Bar dataKey="count">{byStatus.map((s) => <Cell key={s.status} fill={s.fill} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
      <Card title="Spedizioni recenti" className="mt-6">
        {stats.recent.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="dashboard-recent-empty">Nessuna spedizione. Crea la prima spedizione per generare un codice tracking.</p>
        ) : (
          <div className="overflow-x-auto -mx-5 sm:-mx-6">
            <table className="w-full text-sm min-w-[640px]" data-testid="dashboard-recent-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 sm:px-6 py-2">Codice</th><th className="py-2">Destinatario</th><th className="py-2">Tratta</th><th className="py-2">Stato</th><th className="py-2 pr-6">Consegna prevista</th>
              </tr></thead>
              <tbody>
                {stats.recent.map((s) => (
                  <tr key={s.code} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/spedizioni/${s.code}`)} data-testid={`recent-row-${s.code}`}>
                    <td className="px-5 sm:px-6 py-3 font-mono font-bold">{s.code}</td>
                    <td className="py-3">{s.recipient_name}</td>
                    <td className="py-3 text-slate-600">{s.origin_city} → {s.destination_city}</td>
                    <td className="py-3"><StatusBadge status={s.status} /></td>
                    <td className="py-3 pr-6">{fmtDate(s.expected_delivery)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
